import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, apiIssues } from "@/lib/api";

const notifyStock = () => window.dispatchEvent(new Event("sk-stock-changed"));

export function useAssistantChat() {
  const [conv, setConv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [busyMsg, setBusyMsg] = useState(null);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/ai/conversations/current").then((r) => setConv(r.data)).finally(() => setLoading(false));
  }, []);

  const touch = () => setVersion((v) => v + 1);

  const send = useCallback(async (text) => {
    const pending = { id: `tmp-${Date.now()}`, role: "user", text, pending: true };
    setConv((c) => ({ ...(c || { id: null, messages: [] }), messages: [...(c?.messages || []), pending] }));
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/ai/chat", { conversation_id: conv?.id || null, text });
      setConv(data);
      touch();
      return true;
    } catch (e) {
      setConv((c) => ({ ...c, messages: c.messages.filter((m) => m.id !== pending.id) }));
      setError(apiError(e));
      toast.error(apiError(e), { duration: 6000 });
      return false;
    } finally {
      setSending(false);
    }
  }, [conv?.id]);

  const act = useCallback(async (mid, action) => {
    setBusyMsg(mid);
    try {
      const { data } = await api.post(`/ai/conversations/${conv.id}/messages/${mid}/${action}`);
      setConv(data);
      if (action === "confirm") {
        const p = data.messages.find((m) => m.id === mid)?.proposal;
        toast.success(`Proposition validée — ${p?.result?.movements ?? 0} mouvement(s) enregistré(s).`);
        notifyStock();
      } else toast("Proposition annulée — aucune modification.");
      touch();
    } catch (e) {
      const issues = apiIssues(e);
      toast.error(issues.length ? `${apiError(e)} ${issues[0]}` : apiError(e), { duration: 7000 });
      const { data } = await api.get(`/ai/conversations/${conv.id}`);
      setConv(data);
    } finally {
      setBusyMsg(null);
    }
  }, [conv?.id]);

  const open = useCallback(async (id) => { setConv((await api.get(`/ai/conversations/${id}`)).data); }, []);
  const reset = useCallback(async () => { setConv((await api.post("/ai/conversations")).data); touch(); }, []);

  return { conv, loading, sending, busyMsg, version, error, send, confirm: (m) => act(m, "confirm"), cancel: (m) => act(m, "cancel"), open, reset, setConv };
}
